import { useState, useMemo, useRef, useEffect } from 'react';
import { useNostrQuery } from '../hooks/useNostrQuery';
import { PLEDGE_KIND } from '../lib/crowdfund';
import {
  getActivityBadges,
  ACTIVITY_CATEGORY_LABELS,
  type ActivityBadge,
  type ActivitySignals,
} from '../lib/badges';

// ── Activity signals hook ─────────────────────────────────────────
//
// The badges are derived from the same kinds the trust score uses, but this
// hook runs INDEPENDENTLY of useTrustScore. That matters because the trust
// hook returns no `signals` object when the score comes from cache, and it
// short-circuits entirely for the operator — both of which left the badge
// row invisible. This hook always queries, so badges show for everyone.

const ACTIVITY_LOOKBACK_DAYS = 120;

/**
 * Query a user's recent activity and reduce it to the signals the recognition
 * badges need. Cheap enough for a profile page (a handful of capped queries),
 * but NOT meant for feeds — call it once per profile, not per post.
 */
export function useActivitySignals(pubkey: string | undefined): {
  signals: ActivitySignals | null;
  loading: boolean;
} {
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400 * ACTIVITY_LOOKBACK_DAYS);

  const { events: posts } = useNostrQuery({
    filters: pubkey ? [{ kinds: [1], authors: [pubkey], since, limit: 400 }] : [],
    enabled: !!pubkey,
    allRelays: true,
  });

  const { events: tipsSent } = useNostrQuery({
    filters: pubkey ? [{ kinds: [9735], authors: [pubkey], since, limit: 200 }] : [],
    enabled: !!pubkey,
  });

  const { events: tipsReceived } = useNostrQuery({
    filters: pubkey ? [{ kinds: [9735], '#p': [pubkey], since, limit: 200 }] : [],
    enabled: !!pubkey,
  });

  const { events: pledges } = useNostrQuery({
    filters: pubkey ? [{ kinds: [PLEDGE_KIND], authors: [pubkey], limit: 100 }] : [],
    enabled: !!pubkey,
  });

  const signals = useMemo<ActivitySignals | null>(() => {
    if (!pubkey) return null;
    if (posts.length === 0 && tipsSent.length === 0 && tipsReceived.length === 0 && pledges.length === 0) {
      return null;
    }

    const replies = posts.filter((e) => e.tags?.some((t: string[]) => t[0] === 'e'));
    const originals = posts.filter((e) => !e.tags?.some((t: string[]) => t[0] === 'e'));

    const daySet = new Set<string>();
    for (const e of posts) {
      const d = new Date(e.created_at * 1000);
      daySet.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
    }

    return {
      postCount: originals.length,
      meaningfulReplyCount: replies.length,
      tipsSentCount: tipsSent.length,
      tipsReceivedCount: tipsReceived.length,
      crowdfundContributions: pledges.length,
      activeDays: daySet.size,
    };
  }, [pubkey, posts, tipsSent, tipsReceived, pledges]);

  const loading = signals === null && !!pubkey;
  return { signals, loading };
}

interface ActivityBadgesProps {
  /**
   * The profile to show recognition badges for. The component fetches the
   * activity signals itself.
   */
  pubkey: string | undefined;
  /**
   * Layout variant:
   *  - `card`: a titled panel used on the profile.
   *  - `inline`: a bare wrap of chips with no heading.
   */
  variant?: 'card' | 'inline';
  /** Optional heading override for the card variant. */
  title?: string;
  /**
   * When true (typically your own profile) an empty state is shown explaining
   * how to earn badges, instead of rendering nothing.
   */
  showEmptyState?: boolean;
}

/**
 * Activity-Based Recognition Badges.
 *
 * PUBLIC badges earned through measurable on-platform activity — posting,
 * replying, tipping, being tipped, backing crowdfunds, and showing up
 * consistently. Distinct from the private trust score, which is never shown.
 */
export default function ActivityBadges({
  pubkey,
  variant = 'card',
  title = 'Recognition',
  showEmptyState = false,
}: ActivityBadgesProps) {
  const { signals, loading } = useActivitySignals(pubkey);
  const badges = useMemo(() => getActivityBadges(signals), [signals]);

  // Nothing earned yet.
  if (badges.length === 0) {
    if (!showEmptyState || loading) return null;
    // Own profile with no badges yet — show an encouraging empty state.
    return (
      <div className="mt-3 rounded-xl border border-border bg-card p-3">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
          {title}
        </h3>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Earn recognition badges by posting, replying, sending and receiving tips,
          backing crowdfunds, and staying active. They'll appear here as you go.
        </p>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {badges.map((b) => (
          <ActivityBadgeChip key={b.id} badge={b} signals={signals!} />
        ))}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-border bg-card p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {title}
        </h3>
        <span className="text-[10px] text-muted-foreground">
          {badges.length} badge{badges.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {badges.map((b) => (
          <ActivityBadgeChip key={b.id} badge={b} signals={signals!} />
        ))}
      </div>
    </div>
  );
}

function ActivityBadgeChip({ badge, signals }: { badge: ActivityBadge; signals: ActivitySignals }) {
  const [showInfo, setShowInfo] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showInfo) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setShowInfo(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showInfo]);

  // Current value for this badge's category, for the "progress to next" line.
  const current = useMemo(() => {
    switch (badge.category) {
      case 'poster': return signals.postCount;
      case 'conversationalist': return signals.meaningfulReplyCount;
      case 'tipper': return signals.tipsSentCount;
      case 'beloved': return signals.tipsReceivedCount;
      case 'backer': return signals.crowdfundContributions;
      case 'regular': return signals.activeDays;
    }
  }, [badge.category, signals]);

  const toNext = badge.nextThreshold ? Math.max(0, badge.nextThreshold - current) : 0;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setShowInfo((v) => !v)}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${badge.bgColor} ${badge.color} hover:opacity-80 transition-opacity`}
      >
        <span>{badge.emoji}</span>
        <span>{badge.name}</span>
      </button>

      {showInfo && (
        <div className="absolute left-0 top-full mt-1.5 z-30 w-56 bg-popover border border-border rounded-xl shadow-xl p-3 space-y-2">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-base border ${badge.bgColor}`}>
              {badge.emoji}
            </div>
            <div>
              <p className={`text-sm font-bold ${badge.color}`}>{badge.name}</p>
              <p className="text-[10px] text-muted-foreground">
                {ACTIVITY_CATEGORY_LABELS[badge.category]}
              </p>
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground leading-relaxed">
            {badge.description}
          </p>

          {badge.nextThreshold && (
            <div className="pt-1.5 border-t border-border">
              <p className="text-[10px] text-muted-foreground">
                <span className="font-semibold text-foreground">{toNext}</span> more to reach the next tier
                <span className="text-muted-foreground"> ({badge.nextThreshold} total)</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
